import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import DynamicTSLRanges from './DynamicTSLRanges';
import { TSLRange } from '../types';
import { brokerAPI, BrokerStatus, BrokerFeatureToggle } from '../services/brokerApi';

interface ConfigFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (config: any) => void;
  config?: any;
  tradeSettings?: any;
  selectedType?: 'BUY' | 'SELL';
  exchange?: 'NSE' | 'BSE';
}

const ConfigForm: React.FC<ConfigFormProps> = ({ isOpen, onClose, onSubmit, config, tradeSettings, selectedType, exchange = 'NSE' }) => {
  const isBSE = exchange === 'BSE';
  const lotSize = isBSE ? 20 : 75;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });

  useEffect(() => {
    if (isOpen) {
      setPosition({
        x: window.innerWidth / 2 - 350,
        y: window.innerHeight / 2 - 300
      });
    }
  }, [isOpen]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, posX: position.x, posY: position.y };
    e.preventDefault();
  }, [position]);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging) return;
    setPosition({
      x: dragStart.current.posX + e.clientX - dragStart.current.mouseX,
      y: dragStart.current.posY + e.clientY - dragStart.current.mouseY
    });
  }, [dragging]);

  const onMouseUp = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging, onMouseMove, onMouseUp]);
  const [formData, setFormData] = useState({
    name: '',
    symbol: 'NIFTY',
    expiry_date: '',
    capital_percent: '',
    strategy_type: 'buy',
    x_value: '',
    y_value: '',
    strikes: '',
    range_min: '',
    range_max: '',
    per_strike_amount: '',
    slippage: '',
    sl_rs_percent: '',
    tsl_rs_percent: '10',
    sl_x: '',
    sl_y: '',
    tgt_rs_percent: '',
    tgt_x: '',
    tgt_y: '',
    status: 'active',
    tsl_ranges: [] as TSLRange[],
    broker: 'DHAN'
  });

  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  const brokerFeature = isBSE ? 'BSE' : 'NSE';
  const isBrokerEnabledFor = (broker: string) =>
    featureToggles.find(t => t.broker === broker && t.feature === brokerFeature)?.is_enabled ?? (broker === 'DHAN');
  useEffect(() => {
    brokerAPI.list().then(res => setBrokers(res.data.filter(b => b.implemented))).catch(() => {});
    brokerAPI.listFeatures().then(res => setFeatureToggles(res.data)).catch(() => {});
  }, []);

  const rangeMin = parseFloat(formData.range_min) || 0;
  const rangeMax = parseFloat(formData.range_max) || 0;
  const perStrikeAmount = parseFloat(formData.per_strike_amount) || 0;
  const isMaxRsInvalid = rangeMax > 0 && rangeMax <= rangeMin;
  const isPerStrikeInvalid = rangeMax > 0 && perStrikeAmount > 0 && perStrikeAmount < rangeMax * lotSize;

  useEffect(() => {
    console.log('ConfigForm useEffect triggered:', { config, isOpen, selectedType });
    if (isOpen) {
      if (config) {
        console.log('Setting form data with config:', config);
        setFormData({
          name: config.name || '',
          symbol: 'NIFTY',
          expiry_date: config.nifty_expiry_date
            ? config.nifty_expiry_date.substring(0, 10)
            : config.sensex_expiry_date
            ? config.sensex_expiry_date.substring(0, 10)
            : '',
          capital_percent: config.capital_percentage || '100',
          strategy_type: config.type?.toLowerCase() || 'buy',
          x_value: config.x_value?.toString() || '',
          y_value: config.y_value?.toString() || '',
          strikes: config.no_of_strikes?.toString() || '',
          range_min: config.range_min || '',
          range_max: config.range_max || '',
          per_strike_amount: config.per_strike_amount || '',
          slippage: config.slippage_percentage || '',
          sl_rs_percent: config.sl_percentage || '',
          tsl_rs_percent: config.tsl_rs_percentage?.toString() || '10',
          sl_x: config.sl_x || '',
          sl_y: config.sl_y || '',
          tgt_rs_percent: config.target_percentage || '',
          tgt_x: config.target_x || '',
          tgt_y: config.target_y?.toString() || '',
          status: config.is_active ? 'active' : 'inactive',
          tsl_ranges: config.tsl_ranges && config.tsl_ranges.length > 0 ? config.tsl_ranges : [{
            entry_start: parseInt((config.type?.toLowerCase() === 'buy' ? config.x_value : config.y_value)?.toString() || '25'),
            entry_peak: 50,
            range: 4
          }],
          broker: config.broker || 'DHAN'
        });
      } else {
        console.log('Resetting form data for new config');
        setFormData({
          name: '',
          symbol: 'NIFTY',
          expiry_date: getNextTuesdayDate(),
          capital_percent: tradeSettings?.capital_percentage?.toString() || '100',
          strategy_type: selectedType?.toLowerCase() || 'buy',
          x_value: '',
          y_value: '',
          strikes: '',
          range_min: '',
          range_max: '',
          per_strike_amount: '',
          slippage: '',
          sl_rs_percent: '',
          tsl_rs_percent: '10',
          sl_x: '',
          sl_y: '',
          tgt_rs_percent: '',
          tgt_x: '',
          tgt_y: '',
          status: 'active',
          tsl_ranges: [{
            entry_start: 25,
            entry_peak: 50,
            range: 4
          }],
          broker: 'DHAN'
        });
      }
    }
  }, [config, isOpen, tradeSettings, selectedType]);

  // Calculate next Tuesday date
  const getNextTuesdayDate = (): string => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    let targetDate: Date;
    if (dayOfWeek === 2) {
      targetDate = today;
    } else if (dayOfWeek === 1) {
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() + 1);
    } else {
      const daysUntilTuesday = dayOfWeek === 0 ? 2 : (9 - dayOfWeek);
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntilTuesday);
    }

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isActive = formData.status === 'active';

    // Helper function to parse number or return 0
    const parseNum = (val: string) => {
      const parsed = parseFloat(val);
      return isNaN(parsed) || val === '' ? 0 : parsed;
    };

    const parseInt = (val: string) => {
      const parsed = Number.parseInt(val);
      return isNaN(parsed) || val === '' ? 0 : parsed;
    };

    // Validation
    const rangeMin = parseNum(formData.range_min);
    const rangeMax = parseNum(formData.range_max);
    const perStrikeAmount = parseNum(formData.per_strike_amount);

    if (rangeMax <= rangeMin) {
      alert('Max Rs must be greater than Range Min Rs');
      return;
    }

    if (perStrikeAmount < rangeMax * lotSize) {
      alert(`Per Strike Rs must be at least ${(rangeMax * lotSize).toFixed(2)} (Max Rs × ${lotSize})`);
      return;
    }

    // Map form data to match backend expectations
    const configData = {
      name: formData.name || 'Untitled Config',
      type: formData.strategy_type.toUpperCase(),
      // NSE uses nifty_expiry_date, BSE uses sensex_expiry_date
      ...(isBSE
        ? { sensex_expiry_date: formData.expiry_date || getNextTuesdayDate() }
        : { nifty_expiry_date: formData.expiry_date || getNextTuesdayDate() }
      ),
      capital_percentage: parseNum(formData.capital_percent) || 100,
      x_value: parseInt(formData.x_value),
      y_value: parseInt(formData.y_value),
      no_of_strikes: parseInt(formData.strikes),
      range_min: parseNum(formData.range_min),
      range_max: parseNum(formData.range_max),
      per_strike_amount: parseNum(formData.per_strike_amount),
      slippage_percentage: parseNum(formData.slippage),
      sl_percentage: parseNum(formData.sl_rs_percent),
      sl_x: parseNum(formData.sl_x),
      sl_y: parseNum(formData.sl_y),
      tsl_percentage: parseNum(formData.tsl_rs_percent),
      tsl_x: 0,
      tsl_y: 0,
      tsl_rs_percentage: parseNum(formData.tsl_rs_percent) || -10,
      tsl_ranges: formData.tsl_ranges && formData.tsl_ranges.length > 0 ? formData.tsl_ranges : [{
        entry_start: parseInt(formData.x_value) || 25,
        entry_peak: 50,
        range: 4
      }],
      target_active: true,
      target_percentage: parseNum(formData.tgt_rs_percent),
      target_x: parseNum(formData.tgt_x),
      target_y: parseNum(formData.tgt_y),
      is_active: isActive,
      entry_active: isActive,
      exit_active: false,
      broker: formData.broker
    };

    console.log('Submitting config data:', configData);
    onSubmit(configData);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 9999,
      pointerEvents: 'none'
    }}>
      <div style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '700px',
        maxWidth: '95vw',
        height: 'auto',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
        pointerEvents: 'all',
        userSelect: dragging ? 'none' : 'auto'
      }}>
        {/* Header */}
        {/* <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 16px 8px 16px',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: 0
          }}>
            ENTRY / EXIT
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#6b7280',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div> */}

        <form onSubmit={handleSubmit} style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Drag Handle */}
          <div
            onMouseDown={onMouseDown}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 8px',
              marginBottom: '10px',
              borderRadius: '6px',
              background: '#e5e7eb',
              cursor: dragging ? 'grabbing' : 'grab',
              userSelect: 'none'
            }}
          >
            <span style={{ fontSize: '11px', color: '#6b7280' }}>⠿ Drag to move</span>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0 2px', fontSize: '16px', lineHeight: 1 }}>✕</button>
          </div>
          {/* Header Section */}
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '12px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '200px 140px 80px 110px 200px',
              gap: '12px',
              alignItems: 'end'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Configuration Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  required
                  placeholder="Enter config name"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  {isBSE ? 'S-Exp Date' : 'N-Exp Date'}
                </label>
                <input
                  type="date"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  required
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  % Cap
                </label>
                <input
                  type="number"
                  name="capital_percent"
                  value={formData.capital_percent}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  min="1"
                  max="100"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Broker
                </label>
                <select
                  name="broker"
                  value={formData.broker}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  <option value="DHAN">Dhan</option>
                  {brokers.filter(b => b.broker !== 'DHAN').map(b => (
                    <option key={b.broker} value={b.broker}>{b.broker.replace('_', ' ')}{!isBrokerEnabledFor(b.broker) ? ' (disabled)' : ''}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, strategy_type: 'buy'})}
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    border: '1px solid #10b981',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: formData.strategy_type === 'buy' ? '#10b981' : 'white',
                    color: formData.strategy_type === 'buy' ? 'white' : '#10b981',
                    transition: 'all 0.2s'
                  }}
                >
                  Buy
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, strategy_type: 'sell'})}
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    border: '1px solid #ef4444',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: formData.strategy_type === 'sell' ? '#ef4444' : 'white',
                    color: formData.strategy_type === 'sell' ? 'white' : '#ef4444',
                    transition: 'all 0.2s'
                  }}
                >
                  Sell
                </button>
              </div>
            </div>
          </div>

          {/* Entry Section */}
          <div style={{
            backgroundColor: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '12px'
          }}>
            <h3 style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#065f46',
              marginBottom: '16px',
              margin: 0
            }}>
              ENTRY
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '70px 70px 60px 70px 70px 140px 70px',
              gap: '12px'
            }}>
              {formData.strategy_type === 'buy' ? (
                <>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px'
                    }}>X ({tradeSettings?.x_percentage || 0}%)</label>
                    <input
                      type="number"
                      name="x_value"
                      value={formData.x_value}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      step="0.01"
                      required
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px'
                    }}>Y ({tradeSettings?.y_percentage || 0}%)</label>
                    <input
                      type="number"
                      name="y_value"
                      value={formData.y_value}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      step="0.01"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px'
                    }}>Y ({tradeSettings?.y_percentage || 0}%)</label>
                    <input
                      type="number"
                      name="y_value"
                      value={formData.y_value}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      step="0.01"
                      required
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px'
                    }}>X ({tradeSettings?.x_percentage || 0}%)</label>
                    <input
                      type="number"
                      name="x_value"
                      value={formData.x_value}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      step="0.01"
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Strikes</label>
                <input
                  type="number"
                  name="strikes"
                  value={formData.strikes}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Min Rs</label>
                <input
                  type="number"
                  name="range_min"
                  value={formData.range_min}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  step="1"
                  onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                  required
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Max Rs</label>
                <input
                  type="number"
                  name="range_max"
                  value={formData.range_max}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: isMaxRsInvalid ? '2px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isMaxRsInvalid ? '#fef2f2' : 'white'
                  }}
                  step="1"
                  onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                  required
                />
                {isMaxRsInvalid && (
                  <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>Must be &gt; Min Rs</div>
                )}
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Per Strike Rs</label>
                <input
                  type="number"
                  name="per_strike_amount"
                  value={formData.per_strike_amount}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: isPerStrikeInvalid ? '2px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isPerStrikeInvalid ? '#fef2f2' : 'white'
                  }}
                  step="1"
                  onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                  required
                />
                {isPerStrikeInvalid && (
                  <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>Min: {(rangeMax * lotSize).toFixed(0)}</div>
                )}
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Slippage %</label>
                <input
                  type="number"
                  name="slippage"
                  value={formData.slippage}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  step="0.01"
                  required
                />
              </div>
            </div>
          </div>

          {/* Exit and TSL Sections - All inputs in one row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '12px',
            marginBottom: '12px'
          }}>
            {/* Exit Section - Stop Loss */}
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px',
              width: 'fit-content'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#991b1b',
                marginBottom: '12px',
                margin: 0
              }}>
                SL
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '60px 60px 60px',
                gap: '8px',
                marginTop: '12px'
              }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Rs %</label>
                  <input
                    type="number"
                    name="sl_rs_percent"
                    value={formData.sl_rs_percent}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}
                    step="0.01"
                    required
                  />
                </div>
                {formData.strategy_type === 'buy' ? (
                  <>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>X</label>
                      <input
                        type="number"
                        name="sl_x"
                        value={formData.sl_x}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1"
                        onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Y</label>
                      <input
                        type="number"
                        name="sl_y"
                        value={formData.sl_y}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1"
                        onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Y</label>
                      <input
                        type="number"
                        name="sl_y"
                        value={formData.sl_y}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1"
                        onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>X</label>
                      <input
                        type="number"
                        name="sl_x"
                        value={formData.sl_x}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1"
                        onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                        required
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid #fca5a5', margin: '12px 0' }} />

              {/* TGT inside SL container */}
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', margin: '0 0 8px 0' }}>TGT</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '60px 60px 60px',
                gap: '8px'
              }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Rs %</label>
                  <input
                    type="number"
                    name="tgt_rs_percent"
                    value={formData.tgt_rs_percent}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                    step="0.01"
                    required
                  />
                </div>
                {formData.strategy_type === 'buy' ? (
                  <>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>X</label>
                      <input type="number" name="tgt_x" value={formData.tgt_x} onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1" onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()} required />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Y</label>
                      <input type="number" name="tgt_y" value={formData.tgt_y} onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1" onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()} required />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Y</label>
                      <input type="number" name="tgt_y" value={formData.tgt_y} onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1" onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()} required />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>X</label>
                      <input type="number" name="tgt_x" value={formData.tgt_x} onChange={handleChange}
                        style={{ width: '100%', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                        step="1" onKeyDown={(e) => ['.', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()} required />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* TSL Section - Trailing Stop Loss */}
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #7dd3fc',
              borderRadius: '6px',
              padding: '12px',
              width: 'fit-content'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', margin: 0 }}>TSL</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '10px', color: '#6b7280' }}>Rs %</label>
                  <input
                    type="number"
                    name="tsl_rs_percent"
                    value={formData.tsl_rs_percent}
                    onChange={handleChange}
                    style={{ width: '60px', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                    step="0.01"
                    required
                  />
                </div>
                {formData.tsl_ranges.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={formData.tsl_ranges[0].entry_start}
                      disabled
                      style={{ width: '50px', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#f3f4f6' }}
                      step="1"
                    />
                    <input
                      type="number"
                      value={formData.tsl_ranges[0].entry_peak}
                      onChange={(e) => {
                        const updated = [...formData.tsl_ranges];
                        updated[0] = { ...updated[0], entry_peak: parseFloat(e.target.value) };
                        if (updated.length > 1) updated[1] = { ...updated[1], entry_start: parseFloat(e.target.value) + 1 };
                        setFormData({ ...formData, tsl_ranges: updated });
                      }}
                      style={{ width: '50px', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                      step="1"
                    />
                    <input
                      type="number"
                      value={formData.tsl_ranges[0].range}
                      onChange={(e) => {
                        const updated = [...formData.tsl_ranges];
                        updated[0] = { ...updated[0], range: parseFloat(e.target.value) };
                        setFormData({ ...formData, tsl_ranges: updated });
                      }}
                      style={{ width: '45px', padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                      step="1"
                    />
                  </div>
                )}
              </div>

              <DynamicTSLRanges
                xValue={parseInt(formData.strategy_type === 'buy' ? formData.x_value : formData.y_value) || 25}
                tslRanges={formData.tsl_ranges}
                onChange={(ranges) => setFormData({...formData, tsl_ranges: ranges})}
              />
            </div>
          </div>

          {/* Target Section - removed, now inside SL container */}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb',
            marginTop: 'auto',
            flexShrink: 0
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: '#10b981',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {config ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigForm;